import type { PersonId } from "../data/people";
import type { Busy, Status, Task } from "../types";

/** Сетка календаря: 9:00–19:00. Рабочий день — до 18:00, с 13 до 14 обед */
export const VIEW_START = 9;
export const VIEW_END = 19;
export const WORK_END = 18;
export const LUNCH: [number, number] = [13, 14];
export const HOURS = Array.from({ length: VIEW_END - VIEW_START + 1 }, (_, i) => VIEW_START + i);

/** Задача, которую сейчас ставят: показываем её в календаре до отправки */
export interface Draft {
  editor: PersonId;
  day: number;
  title: string;
  estimate: number;
}

export interface Slot {
  key: string;
  kind: "task" | "proposal" | "busy" | "meet" | "draft";
  title: string;
  hours: number;
  task?: Task;
  /** куски блока — задачу прерывают обед и планёрки */
  segs: [number, number][];
  /** по раскладке не успевает к сроку */
  late?: boolean;
}

export interface DayPlan {
  slots: Slot[];
  /** часы с учётом черновика */
  load: number;
  /** с какого часа монтажёр свободен (без черновика) */
  freeAt: number;
}

const ORDER: Record<Status, number> = { open: 6, done: 0, publish: 1, review: 2, rework: 3, work: 4, wait: 5 };

/**
 * У задач «день в день» нет времени начала, поэтому раскладываем день подряд с 9:00:
 * сначала сделанное и то, что сдаётся раньше, потом остальная работа и предложенные переносы.
 * Обед и планёрки обходим. Так видно, до скольки монтажёр занят и где начинается переработка.
 */
export function dayPlan(editor: PersonId, day: number, tasks: Task[], busy: Busy[], opts: { draft?: Draft; excludeTaskId?: string } = {}): DayPlan {
  const mine = tasks.filter((t) => t.editor === editor && t.id !== opts.excludeTaskId && (t.proposal ? t.proposal.day : t.day) === day);
  const dayBusy = busy.filter((b) => b.editor === editor && b.day === day);
  const blocked: [number, number][] = [LUNCH];
  const slots: Slot[] = [];

  let meetAt = 10;
  dayBusy
    .filter((b) => b.kind === "meet")
    .forEach((b) => {
      blocked.push([meetAt, meetAt + b.hours]);
      slots.push({ key: b.id, kind: "meet", title: b.title, hours: b.hours, segs: [[meetAt, meetAt + b.hours]] });
      meetAt += b.hours;
    });

  let cursor = VIEW_START;
  const place = (len: number) => {
    const segs: [number, number][] = [];
    let t = cursor;
    let left = len;
    while (left > 0.001) {
      const inside = blocked.find(([a, b]) => t >= a && t < b);
      if (inside) {
        t = inside[1];
        continue;
      }
      const next = blocked.reduce((m, [a]) => (a > t ? Math.min(m, a) : m), Infinity);
      const end = Math.min(t + left, next);
      segs.push([t, end]);
      left -= end - t;
      t = end;
    }
    cursor = t;
    return segs;
  };

  mine
    .filter((t) => !t.proposal)
    .sort((a, b) => ORDER[a.status] - ORDER[b.status] || a.due - b.due)
    .forEach((t) => {
      const segs = place(t.estimate);
      const dueH = (t.due - day * 1440) / 60;
      slots.push({ key: t.id, kind: "task", title: t.title, hours: t.estimate, task: t, segs, late: t.status !== "done" && segs[segs.length - 1][1] > dueH + 0.01 });
    });
  dayBusy
    .filter((b) => b.kind !== "meet")
    .forEach((b) => slots.push({ key: b.id, kind: "busy", title: b.title, hours: b.hours, segs: place(b.hours) }));
  mine
    .filter((t) => t.proposal)
    .forEach((t) => slots.push({ key: `p-${t.id}`, kind: "proposal", title: t.title, hours: t.estimate, task: t, segs: place(t.estimate) }));

  // Свободен с: если упёрлись в обед — после обеда
  const freeAt = blocked.reduce((t, [a, b]) => (t >= a && t < b ? b : t), cursor);
  let load = slots.reduce((a, s) => a + s.hours, 0);
  if (opts.draft && opts.draft.editor === editor && opts.draft.day === day) {
    slots.push({ key: "draft", kind: "draft", title: opts.draft.title, hours: opts.draft.estimate, segs: place(opts.draft.estimate) });
    load += opts.draft.estimate;
  }
  return { slots, load, freeAt };
}
